import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
// Presumo que seu CSS esteja na pasta correta
// import '../css/styles.css';

// Registro dos componentes do Chart.js
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

function Gantt3() {
  const { id } = useParams();
  const [projeto, setProjeto] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);

  // --- Função Removida ---
  // A função adicionarDias() não é mais necessária,
  // pois estamos usando as datas reais do banco de dados.

  // Efeito para buscar os dados do projeto
  useEffect(() => {
    axios
      .get(`/api/projetocompleto/${id}`)
      .then((res) => {
        // Pega o primeiro (e único) resultado
        if (res.data.Result && res.data.Result.length > 0) {
          setProjeto(res.data.Result[0]);
        } else {
          console.error("Projeto não encontrado");
        }
      })
      .catch((err) => console.error("Erro ao buscar projeto:", err));
  }, [id]);

  /**
   * Helper para converter string de data (YYYY-MM-DD) para timestamp
   * Retorna null se a data for inválida, nula ou "0000-00-00"
   */
  const parseDate = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00") {
      return null;
    }
    const date = new Date(dateStr);
    // Verifica se a data é válida (ex: new Date(null) pode dar 1970)
    if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
      return null;
    }
    return date.getTime();
  };

  // Efeito para processar os dados do gráfico QUANDO o projeto for carregado
  useEffect(() => {
    if (!projeto) {
      return; // Ainda não carregou o projeto
    }

    // --- MUDANÇA PRINCIPAL ---
    // Mapeia as etapas diretamente das colunas do banco de dados
    const etapasSource = [
      {
        nome: "1 - Estruturação",
        inicio: projeto.data_inicio_etapa1,
        fim: projeto.data_termino_etapa1,
      },
      {
        nome: "2 - TdR e orçamento",
        inicio: projeto.data_inicio_etapa2,
        fim: projeto.data_termino_etapa2,
      },
      {
        nome: "3 - Licitação",
        inicio: projeto.data_inicio_etapa3,
        fim: projeto.data_termino_etapa3,
      },
      {
        nome: "4 - Contrato",
        inicio: projeto.data_inicio_etapa4,
        fim: projeto.data_termino_etapa4,
      },
      {
        nome: "5 - Execução",
        // Com base no seu schema, 'data_ois' parece ser o início da etapa 5
        inicio: projeto.data_ois,
        fim: projeto.data_termino_etapa5,
      },
      {
        nome: "6 - Encerramento",
        inicio: projeto.data_inicio_etapa6,
        fim: projeto.data_termino_etapa6,
      },
    ];

    // Filtra apenas as etapas que têm datas de início E fim válidas
    const etapasValidas = etapasSource
      .map((etapa) => ({
        nome: etapa.nome,
        // Converte as datas para timestamp (que o chart.js usa)
        inicio: parseDate(etapa.inicio),
        fim: parseDate(etapa.fim),
      }))
      .filter(
        (etapa) => etapa.inicio && etapa.fim && etapa.fim >= etapa.inicio
      );

    // Se não houver etapas válidas, não há o que mostrar
    if (etapasValidas.length === 0) {
      setChartData(null); // Limpa dados antigos
      setChartOptions(null);
      return;
    }

    // Encontra a data de início mínima e máxima de TODAS as etapas válidas
    // para definir a escala (eixo X) do gráfico dinamicamente
    const allDates = etapasValidas.flatMap((e) => [e.inicio, e.fim]);
    const minDate = Math.min(...allDates);
    const maxDate = Math.max(...allDates);

    // Formata os dados para o Chart.js
    const dataValues = etapasValidas.map((etapa) => [etapa.inicio, etapa.fim]);
    const labels = etapasValidas.map((e) => e.nome);
    const colors = [
      "rgba(52, 152, 219, 0.7)",
      "rgba(46, 204, 113, 0.7)",
      "rgba(155, 89, 182, 0.7)",
      "rgba(241, 196, 15, 0.7)",
      "rgba(230, 126, 34, 0.7)",
      "rgba(52, 73, 94, 0.7)",
    ];

    // Define os dados do gráfico
    setChartData({
      labels: labels,
      datasets: [
        {
          label: "Período da Etapa",
          data: dataValues,
          // Garante que só usamos as cores necessárias
          backgroundColor: colors.slice(0, dataValues.length),
          borderColor: colors
            .slice(0, dataValues.length)
            .map((c) => c.replace("0.7", "1")),
          borderWidth: 1,
          borderSkipped: false,
          borderRadius: 6,
          barPercentage: 0.6,
        },
      ],
    });

    // Define as opções do gráfico
    setChartOptions({
      indexAxis: "y", // Gráfico de barras horizontais
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: function (context) {
              const start = new Date(context.raw[0]);
              const end = new Date(context.raw[1]);
              // Calcula a diferença em dias (adiciona 1 para ser inclusivo)
              const dias =
                Math.round(
                  (context.raw[1] - context.raw[0]) / (1000 * 60 * 60 * 24)
                ) + 1;
              return `De ${start.toLocaleDateString()} até ${end.toLocaleDateString()} (${dias} dias)`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          // Eixo X dinâmico com base nas datas reais
          min: minDate,
          max: maxDate,
          ticks: {
            // Formata os labels do eixo X como datas legíveis
            callback: function (value) {
              return new Date(value).toLocaleDateString();
            },
            font: { size: 10 },
          },
          grid: { drawBorder: false },
        },
        y: {
          ticks: { font: { size: 12 } },
          grid: { display: false },
        },
      },
    });
  }, [projeto]); // Este efeito depende do 'projeto' ser carregado

  // ----- RENDERIZAÇÃO -----

  // Estado de carregamento
  if (!projeto) {
    return (
      <main className="main-container">
        <div>Carregando...</div>
      </main>
    );
  }

  // Renderização principal
  return (
    <main className="main-container">
      <div className="containerdashboard">
        <h1>Projeto: {projeto.nomeprojeto}</h1>
        {/* Mostra a data de início da primeira etapa como a data do projeto */}
        {projeto.data_inicio_etapa1 && (
          <h4>
            Início: {new Date(projeto.data_inicio_etapa1).toLocaleDateString()}
          </h4>
        )}
        <Link to="/projetocompleto" className="btn btn-info px-5 me-2">
          Voltar
        </Link>
      </div>

      <div className="containerdashboard">
        <h5>Gráfico de Gantt Real</h5>
        <div className="Gragrantt" style={{ height: "500px" }}>
          {/* Só renderiza o gráfico se os dados e opções estiverem prontos */}
          {chartData && chartOptions ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <div>
              Não há datas válidas (início e fim) cadastradas para as etapas
              deste projeto.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default Gantt3;
